import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Phone, Mail, MapPin, Send, CalendarClock, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import Reveal from "../components/Reveal";
import { PageHero } from "./Services";
import { company, faqs } from "../data/mock";
import Seo from "../components/Seo";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CORE = ["name", "email", "phone", "business", "service", "message"];

const Contact = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [loadingForm, setLoadingForm] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/form-config`)
      .then((res) => {
        setFields(res.data);
        const init = {};
        res.data.forEach((f) => { init[f.key] = ""; });
        setValues(init);
      })
      .catch(() => setFields([]))
      .finally(() => setLoadingForm(false));
  }, []);

  const update = (key, val) => setValues((v) => ({ ...v, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = fields.filter((f) => f.required && !String(values[f.key] || "").trim());
    if (missing.length) {
      toast({ title: "A few fields are required", description: `Please fill in: ${missing.map((f) => f.label).join(", ")}.`, variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const core = {}; const answers = {};
    fields.forEach((f) => {
      const v = values[f.key] || "";
      if (CORE.includes(f.key)) core[f.key] = v;
      else answers[f.key] = v;
    });
    try {
      await axios.post(`${API}/leads`, { ...core, answers });
      const cleared = {};
      fields.forEach((f) => { cleared[f.key] = ""; });
      setValues(cleared);
      navigate("/thank-you");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({ title: "Something went wrong", description: typeof detail === "string" ? detail : "We couldn't send your message. Please try again or give us a call.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (f) => {
    if (f.type === "textarea") {
      return <Textarea id={f.key} rows={4} value={values[f.key] || ""} onChange={(e) => update(f.key, e.target.value)} placeholder={f.placeholder} />;
    }
    if (f.type === "select") {
      return (
        <Select value={values[f.key] || ""} onValueChange={(v) => update(f.key, v)}>
          <SelectTrigger><SelectValue placeholder={f.placeholder || "Select an option"} /></SelectTrigger>
          <SelectContent>
            {(f.options || []).map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    return <Input id={f.key} type={f.type || "text"} value={values[f.key] || ""} onChange={(e) => update(f.key, e.target.value)} placeholder={f.placeholder} />;
  };

  const contactCards = [
    { icon: Phone, title: "Call us", value: company.phone, href: company.phoneHref },
    { icon: Mail, title: "Email us", value: company.email, href: company.emailHref },
    { icon: MapPin, title: "Visit us", value: `${company.address.line1}, ${company.address.line2}`, href: "#map" },
  ];

  return (
    <div className="overflow-hidden">
      <Seo title="Contact Us — Free Marketing Consultation" description="Reach out to Jellis Enterprise for a free, no-obligation marketing consultation. Call (760) 664-2227 or send us a message today." />
      <PageHero
        eyebrow="Contact"
        title="Let’s grow your business together"
        subtitle="Get the most out of your marketing with experts who handle it all. Reach out for a free, no-obligation consultation."
      />

      {/* Contact cards */}
      <section className="relative -mt-12 z-10">
        <div className="mx-auto max-w-7xl px-6 grid sm:grid-cols-3 gap-5">
          {contactCards.map((c, i) => (
            <Reveal key={c.title} delay={i * 80}>
              <a href={c.href} className="flex items-start gap-4 rounded-2xl bg-white border border-slate-200 shadow-lg p-6 card-lift">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl brand-gradient shadow-lg shrink-0">
                  <c.icon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-500">{c.title}</p>
                  <p className="mt-1 font-semibold text-slate-900 break-words">{c.value}</p>
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-6 grid lg:grid-cols-2 gap-12 items-start">
          {/* Form */}
          <Reveal>
            <div className="rounded-3xl border border-slate-200 bg-white shadow-xl p-8 md:p-10">
              <h2 className="font-display text-2xl font-extrabold text-slate-900">Tell us about your business</h2>
              <p className="mt-2 text-slate-600">The more we know, the better we can help. We’ll reply within one business day.</p>
              {loadingForm ? (
                <div className="mt-8 text-slate-400">Loading form...</div>
              ) : (
                <form onSubmit={handleSubmit} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {fields.map((f) => (
                    <div key={f.key} className={`space-y-2 ${f.width === "full" ? "sm:col-span-2" : ""}`}>
                      <Label htmlFor={f.key}>{f.label}{f.required && " *"}</Label>
                      {f.help_text && <p className="text-xs text-slate-500 -mt-1">{f.help_text}</p>}
                      {renderField(f)}
                    </div>
                  ))}
                  <div className="sm:col-span-2">
                    <Button type="submit" size="lg" disabled={submitting} className="w-full brand-gradient text-white font-bold text-base group">
                      {submitting ? "Sending..." : "Send Message"}
                      <Send className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </Reveal>

          {/* Right column */}
          <Reveal delay={120} className="space-y-6">
            <div className="rounded-3xl navy-gradient p-8 md:p-10 text-white relative overflow-hidden">
              <div className="blob h-48 w-48 bg-blue-500 -top-8 right-0" />
              <div className="relative z-10">
                <div className="flex items-center gap-3">
                  <CalendarClock className="h-6 w-6 text-blue-300" />
                  <h3 className="font-display text-xl font-bold">Prefer to book a call?</h3>
                </div>
                <p className="mt-3 text-blue-100/80">
                  Grab a 30-minute slot that works for you. It’s free, and there’s zero obligation to sign up.
                </p>
                <a href={company.bookingUrl} target="_blank" rel="noreferrer">
                  <Button size="lg" className="mt-6 bg-white text-blue-700 hover:bg-blue-50 font-bold w-full">
                    Schedule a Call Today
                  </Button>
                </a>
                <div className="mt-6 flex items-center gap-2 text-sm text-blue-100/70">
                  <Clock className="h-4 w-4" /> Mon–Fri, 8:00am – 6:00pm CST
                </div>
              </div>
            </div>

            {/* Map */}
            <div id="map" className="rounded-3xl overflow-hidden border border-slate-200 shadow-lg h-[280px]">
              <iframe
                title="Jellis Enterprise location"
                width="100%"
                height="100%"
                loading="lazy"
                style={{ border: 0 }}
                referrerPolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps?q=6834+Cantrell+Rd,+Little+Rock,+AR+72207&output=embed"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad bg-slate-50">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal className="text-center">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-600">FAQ</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-extrabold text-slate-900">Questions? We’ve got answers</h2>
          </Reveal>
          <Reveal delay={100} className="mt-10">
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="rounded-2xl border border-slate-200 bg-white px-6">
                  <AccordionTrigger className="text-left font-display font-bold text-slate-900 hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
